import { type FC } from 'react'
import { Button } from '../../../../../components/Button/Button'
import { type EventTypeHost } from '../../../../../models/Scheduling'
import { HostRow } from './HostRow'
import { EmptyHostsBox, type HostsNotice, HostsList, HostsSection } from './HostsSection'

interface RotatingHostsSectionProps {
  hosts: EventTypeHost[]
  disabled: boolean
  onAdd: () => void
  onRemove: (userId: string) => void
}

const notice = (hosts: EventTypeHost[]): HostsNotice | null => {
  if (hosts.length === 0) {
    return null
  }
  const ineligibleCount = hosts.filter((host) => !host.eligible).length
  if (ineligibleCount === hosts.length) {
    return { tone: 'warning', text: "No host can take bookings. Clients can't book this event type." }
  }
  if (ineligibleCount > 0) {
    return {
      tone: 'info',
      text: `${ineligibleCount} host${ineligibleCount === 1 ? " can't" : "s can't"} take bookings. Bookings still go to the other hosts.`,
    }
  }
  return null
}

export const RotatingHostsSection: FC<RotatingHostsSectionProps> = ({ hosts, disabled, onAdd, onRemove }) => (
  <HostsSection
    title='Hosts'
    subtitle='Clients can only book hosts who are active and have a connected calendar.'
    notice={notice(hosts)}
    action={
      !disabled && hosts.length > 0 ? (
        <Button type='button' variant='primary' className='text-nowrap' onClick={onAdd}>
          <i className='bi bi-plus-circle me-1' aria-hidden='true' />
          Add hosts
        </Button>
      ) : null
    }
  >
    {hosts.length === 0 ? (
      <EmptyHostsBox
        text="No hosts assigned. Clients can't book this event type."
        actionLabel={disabled ? undefined : 'Set up hosts'}
        actionIcon='bi-plus-circle'
        onAction={onAdd}
      />
    ) : (
      <HostsList>
        {hosts.map((host) => (
          <HostRow
            key={host.userId}
            host={host}
            action={
              disabled ? null : (
                <Button
                  type='button'
                  variant='grayButton'
                  className='p-2'
                  aria-label={`Remove ${host.name ?? host.email ?? host.userId}`}
                  onClick={() => onRemove(host.userId)}
                >
                  <i className='bi bi-trash' aria-hidden='true' />
                </Button>
              )
            }
          />
        ))}
      </HostsList>
    )}
  </HostsSection>
)
